import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeUnits, useRealtimeDrivers, useRealtimeTags } from 'Core/hooks';
import { useIntl } from 'react-intl';

import { TagsGroup } from 'Components/Tag';

import {
    Grid,
    Icon,
    Typography
} from "@mui/material";

import {
    Pill
} from 'Components';

import { getTagColor } from 'Core/data/Helpers';

import { SearchInput } from 'Components/uiControls';
import {
    catalogLoad
} from 'Redux/actions';

// Styles
import './styles.scss';

const EntityTagViewer = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const {
        entity,
        selectedEntities,
        selectedTags,
        height = '100%',
        tab = 0
    } = props;

    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
    const loadingDrivers = useSelector(state => state.catalogsRedux.loadingDrivers);
    const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
    //const unitsList = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
    const loadingUnits = useSelector(state => state.catalogsRedux.loadingUnits);
    const subusers = useSelector(state => state.catalogsRedux.subusers);
    const loadingSubusers = useSelector(state => state.catalogsRedux.loadingSubusers);
    const forms = useSelector(state => state.catalogsRedux.forms);
    const loadingForms = useSelector(state => state.catalogsRedux.loadingForms);
    const loadingAssets = useSelector(state => state.catalogsRedux.loadingAssets);
    const loadingReadings = useSelector(state => state.catalogsRedux.loadingReadings);
    const landmarks = useSelector((state) => state.catalogsRealtimeRedux.landmarks);
    const loadingLandmarks = useSelector((state) => state.catalogsRealtimeRedux.loadingLandmarks);
    const geofences = useSelector((state) => state.catalogsRealtimeRedux.geofences);
    const loadingGeofences = useSelector((state) => state.catalogsRealtimeRedux.loadingGeofences);
    const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
    const loadingTags = useSelector(state => state.catalogsRedux.loadingTags);

    const availableEntities = [
        { id: "Unit", label: messages['entityTagViewer_units'] },
        { id: "Driver", label: messages['entityTagViewer_drivers'] },
        { id: "Landmark", label: messages['entityTagViewer_landmarks'] },
        { id: "Geofence", label: messages['entityTagViewer_geofences'] },
        { id: "Form", label: messages['entityTagViewer_forms'] },
        { id: "User", label: messages['entityTagViewer_users'] },
    ];

    const options = [];
    let selectedEntity = {};
    if (entity) {
        selectedEntity = availableEntities.find(av => av?.id === entity);
        if (selectedEntity) options.push(selectedEntity);
    }

    if (selectedTags?.length > 0) {
        options.push({ id: "Tag", label: messages['entityTagViewer_tag'] });
    }
    const [searchText, setSearchText] = useState("");

    const [selectedOption, setSelectedOption] = useState(!tab || !selectedTags?.length ? selectedEntity : { id: "Tag", label: messages['entityTagViewer_tag'] });

    const [data] = useMemo(() => {
        let tempData = [];
        let ids = selectedEntities.map(selectedEntity => parseInt(selectedEntity?.id));
        let tagIds = selectedTags.map(selectedTag => parseInt(selectedTag?.id));

        if (selectedOption?.id === "Tag") {
            tempData = tags.filter(tag => tagIds?.includes(parseInt(tag?.id)));
        } else if (selectedOption?.id === "Unit") {
            tempData = units.filter(unit => ids?.includes(parseInt(unit?.id)));
        } else if (selectedOption?.id === "Driver") {
            tempData = drivers.filter(driver => ids?.includes(parseInt(driver?.driverId)));
        }

        let viewData = tempData;
        if (searchText && typeof searchText !== 'undefined' && searchText.length > 0) {
            viewData = tempData.filter(x => {
                let result = false;
                let label = x?.name || x?.label || x?.userName || "";
                if (label && label?.toLowerCase().indexOf(searchText.toLowerCase()) >= 0) {
                    result = true;
                }
                return result;
            });
        }

        return [viewData];
    });

    return (
        <div>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
                style={{
                    marginBottom: 10
                }}
            >
                {
                    options.map((newOption, index) => {
                        return (
                            <Grid item xs key={index}>
                                <Pill
                                    key={index}
                                    textField="label"
                                    item={newOption}
                                    outlined={newOption?.id !== selectedOption?.id}
                                    onClick={() => {
                                        setSelectedOption(newOption);
                                    }}
                                    style={{ border: '0', width: '100%' }}
                                    colorField="default"
                                    colorSelection="005bf8"
                                />
                            </Grid>
                        )
                    })
                }
            </Grid>

            <div
                style={{
                    marginBottom: 25
                }}
            >
                <SearchInput
                    placeholder={messages['search']}
                    searchText={searchText}
                    disabled={loadingUnits || loadingDrivers || loadingLandmarks || loadingGeofences || loadingSubusers || loadingForms || loadingAssets}
                    onSearchText={(text) => {
                        setSearchText(text);
                    }}
                />
            </div>

            {
                data?.length == 0 ?
                    <span className="no-more-items" style={{ padding: 0 }}>
                        {messages['entityTagViewer_notFound']}
                    </span> :
                    <React.Fragment>
                        {
                            selectedOption?.id !== "Tag" &&
                            <div
                                style={{
                                    overflow: "hidden auto",
                                    height: height
                                }}
                            >
                                {data.map((item, index) => {
                                    return (
                                        <Grid
                                            container
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            key={index}
                                            wrap="nowrap"
                                            spacing={4}
                                        >
                                            <Grid item xs={4} sm={4}>
                                                <TagsGroup
                                                    options={(item?.tags || [])?.map(t => {
                                                        return {
                                                            ...t,
                                                            color: `#${getTagColor(t?.color)}`
                                                        }
                                                    })}
                                                    nameField="label"
                                                    colorField="color"
                                                />
                                            </Grid>
                                            <Grid item xs={8} sm={8}>
                                                <Typography noWrap style={{ fontWeight: "bold" }}>
                                                    {item?.label || item?.name || item?.userName || ""}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    )
                                })}
                            </div>
                        }

                        {
                            selectedOption?.id == "Tag" &&
                            <div
                                style={{
                                    overflow: "hidden auto",
                                    height: height
                                }}
                            >
                                {data.map((item, index) => {
                                    return (
                                        <Grid
                                            container
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            key={index}
                                            wrap="nowrap"
                                            spacing={1}
                                        >
                                            <Grid item>
                                                <Icon
                                                    style={{
                                                        fontSize: 17,
                                                        margin: '5px 2px 0px',
                                                        color: `#${getTagColor(item?.color)}`
                                                    }}
                                                >
                                                    local_offer
                                                </Icon>
                                            </Grid>
                                            <Grid item xs zeroMinWidth>
                                                <Typography noWrap style={{ fontWeight: "bold" }}>
                                                    {item?.label}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    )
                                })}
                            </div>
                        }
                    </React.Fragment>
            }
        </div>
    )
}

export default EntityTagViewer;